import Delete from "@mui/icons-material/Delete";
import { type GridRowSelectionModel } from "@mui/x-data-grid-pro";
import {
	CircularProgress, Container,
	Stack
} from "@mui/material";
import axios, { type AxiosError } from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import apiRoutes from "../../../../constants/api-routes";
import messageLevels from "../../../../constants/message-levels";
import messages from "../../../../constants/messages";
import navigationItemReferences from "../../../../constants/navigation-item-references";
import pages from "../../../../constants/pages";
import {
	emptyGridRowSelectionModel, gridRowSelectionIdsArray,
	gridRowSelectionSize, resolveGridSelectedRowIds
} from "../../../../helpers/dataGridRowSelectionModel";
import useAuthHeader from "../../../../hooks/useAuthHeader";
import useCoAppNavigation from "../../../../hooks/useCoAppNavigation";
import useDatagridSorting from "../../../../hooks/useDatagridSorting";
import useToastAlert from "../../../../hooks/useToastAlert";
import { selectIsCoAppSuperAdmin, selectOrganizationSsoAndScim } from "../../../../redux/userSlice";
import CoAppDatagridHeader from "../../../global/components/datagrid/CoAppDatagridHeader";
import useCoAppDataGridFilters from "../../../global/components/datagrid/useCoAppDataGridFilters";
import CoAppStandardDataGrid from "../../../global/components/datagrid/CoAppStandardDataGrid";
import CoAppAddItemsToListModal from "../../../global/components/modals/CoAppAddItemsToListModal";
import CoAppDestructiveConfirmationModal from "../../../global/components/modals/CoAppDestructiveConfirmationModal";
import CoAppEditNameModal from "../../../global/components/modals/CoAppEditNameModal";
import SettingsDataGridSelectionRow, { destructiveIconSx } from "../../SettingsDataGridSelectionRow";
import { GroupUser } from "../../../global/types";
import GroupUsersColumns, { groupUserColumns } from "./columns/GroupUsersColumns";

/** Passed from `GroupWithErrorBoundary` when the route was opened without `navigate(..., { state })` (e.g. sidebar). */
interface GroupNavState {
	name: string;
}

interface GroupUsersProps {
	state?: GroupNavState | null;
}

interface UserToAdd {
	id: string | number;
	name: string;
	email: string;
	firstname?: string;
	lastname?: string;
}

export default function GroupUsers({ state: stateFromBoundary }: GroupUsersProps = {}) {
	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();
	const { viewUserPage, updateCoAppNavigation } = useCoAppNavigation();
	const { sortModel, handleSortModelChange, handleInitializeSortModel } = useDatagridSorting("groupUsers");
	const navigate = useNavigate();
	const { groupId } = useParams<{ groupId: string }>();
	const { pathname, state: stateFromLocation } = useLocation() as {
		pathname: string;
		state: GroupNavState | undefined;
	};
	/* Router `location.state` only exists when navigation passes `{ state }`. Otherwise the parent boundary injects `state` after fetching the group. */
	const state = (stateFromBoundary ?? stateFromLocation) as GroupNavState;
	const organizationSsoStatus = useSelector(selectOrganizationSsoAndScim);
	const currentUserIsCoAppSuperAdmin = useSelector(selectIsCoAppSuperAdmin);

	const [addUsersToggled, setAddUsersToggled] = useState<boolean>(false);
	const [availableUsers, setAvailableUsers] = useState<UserToAdd[]>([]);
	const [selectedUsers, setSelectedUsers] = useState<GridRowSelectionModel>(() => emptyGridRowSelectionModel());
	const [groupUsers, setGroupUsers] = useState<GroupUser[]>([]);
	const [userSeed, setUserSeed] = useState<number>(1);
	const [usersToAdd, setUsersToAdd] = useState<UserToAdd[]>([]);
	const [editGroupIsToggled, setEditGroupIsToggled] = useState<boolean>(false);
	const [deleteGroupIsToggled, setDeleteGroupIsToggled] = useState<boolean>(false);
	const [removeUsersIsToggled, setRemoveUsersIsToggled] = useState<boolean>(false);
	const [groupNameInput, setGroupNameInput] = useState<string>("");
	const [groupNameError, setGroupNameError] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(true);
	const [optionsLoading, setOptionsLoading] = useState<boolean>(true);
	
	const init = () => {
		if (!groupId) return;
		
		const userQueryObject = {
			groupId: groupId,
		};
		axios
			.get(apiRoutes.getUsers, {
				headers: authHeader,
				params: userQueryObject,
			})
			.then((res) => {
				const rows = res.data as GroupUser[];
				const normalized = rows.map((user) => {
					const nested = user.user && typeof user.user === "object" ? user.user : undefined;
					const fn = (user.firstname as string | undefined) ?? nested?.firstname;
					const ln = (user.lastname as string | undefined) ?? nested?.lastname;
					const em = (user.email as string | undefined) ?? nested?.email ?? "";
					const displayName =
						typeof user.name === "string" && user.name
							? user.name
							: fn && ln
								? `${fn} ${ln}`.trim()
								: em || String(user.id);
					return {
						...user,
						name: displayName,
						firstname: fn,
						lastname: ln,
						email: em,
						user: {
							id: user.id,
							firstname: fn ?? "",
							lastname: ln ?? "",
							email: em,
						},
					} as GroupUser;
				});
				setGroupUsers(normalized);
				setLoading(false);
			})
			.catch((err: AxiosError) => {
				console.error(err);
			});
	};

	const handleUserStatusToggle = (id: string | number, state: "false" | "true") => {
		const userJSON = {
			active: state === "true"
		};
		axios.put(apiRoutes.setUserActive(id), userJSON, { headers: authHeader })
			.then(() => {
				init();
			})
			.catch((error: AxiosError) => {
				console.error(error);
				handleToastAlert(messageLevels.ERROR, messages.USER_TOGGLE_ERROR_MSG);
			});
	};

	const removeUserFromGroup = (userId: string | number) => {
		if (!groupId) return;
		
		axios
			.delete(apiRoutes.removeUserFromGroup(userId, groupId), {
				headers: authHeader,
			})
			.then(() => {
				init();
			})
			.catch((error: AxiosError) => {
				console.log(error);
				handleToastAlert(messageLevels.ERROR, messages.USER_REMOVED_ERROR_MSG);
			});
	};

	const columns = useMemo(() => (
		GroupUsersColumns({
			organizationSsoStatus: organizationSsoStatus || false,
			handleUserStatusToggle,
			viewUserPage: viewUserPage as any,
			navigationItemReferences,
			groupId: groupId || "",
			stateName: state.name,
			removeUserFromGroup,
			currentUserIsCoAppSuperAdmin
		})
	), [organizationSsoStatus, navigationItemReferences, groupId, state.name, currentUserIsCoAppSuperAdmin]);

	const handleToggleGroupEditDialog = () => {
		setEditGroupIsToggled(!editGroupIsToggled);
		setGroupNameError("");
		setGroupNameInput("");
	};

	const handleGroupNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setGroupNameInput(e.target.value);
		setGroupNameError("");
	};

	const handleGroupNameChangeSubmit = () => {
		if (!groupNameInput || !groupId) return;

		if (groupNameInput === state.name) {
			handleToggleGroupEditDialog();
			return;
		}

		axios.put(apiRoutes.updateGroup(groupId), { name: groupNameInput }, { headers: authHeader })
			.then(() => {
				setEditGroupIsToggled(false);
				updateCoAppNavigation(false, navigationItemReferences.groupNavItemIndex, groupNameInput, pathname);
			})
			.catch((e: AxiosError) => {
				console.error(e);
				if (e.response?.data && typeof e.response.data === "object" && "message" in e.response.data && 
					typeof e.response.data.message === "string" && e.response.data.message.includes("already exists")) {
					setGroupNameError(messages.RESOURCE_NAME_ALREADY_EXISTS_ERROR_MSG("Group"));
					return;
				}
				handleToggleGroupEditDialog();
				handleToastAlert(messageLevels.ERROR, messages.GROUP_NAME_UPDATE_ERROR_MSG);
			});
	};

	const handleToggleDeleteGroupDialog = () => {
		setDeleteGroupIsToggled(!deleteGroupIsToggled);
	};

	const confirmGroupDeletion = () => {
		if (!groupId) return;
		
		axios.delete(apiRoutes.deleteGroup + "/" + groupId, {
			headers: authHeader
		}).then(() => {
			updateCoAppNavigation(true, navigationItemReferences.groupNavItemIndex);
			navigate(pages.groupManagement, { replace: true });
			handleToggleDeleteGroupDialog();
		}).catch((err: AxiosError) => {
			console.error(err);
			handleToastAlert(messageLevels.ERROR, messages.GROUP_DELETION_ERROR_MSG);
			handleToggleDeleteGroupDialog();
		});
	};

	const addUsersToGroup = () => {
		if (!groupId) return;
		
		const userIds = usersToAdd.map(u => u.id);
		const requestJson = {
			users: userIds,
			group: { id: groupId }
		};
		axios.post(apiRoutes.addUsersToGroup, requestJson, {
			headers: authHeader
		}).then(() => {
			toggleAddNewDialog();
		}).catch((err: AxiosError) => {
			console.error(err);
			handleToastAlert(messageLevels.ERROR, messages.USERS_ADDED_ERROR_MSG);
		});
	};

	const addUserToUsersToAdd = (value: string[]) => {
		/* Options come from `availableUsers` only — resolve there to avoid wrong match + in-place grid mutation */
		const mappedValue = value
			.map((displayName) => {
				const found = availableUsers.find((row) => row.name === displayName);
				if (!found) return null;
				const display =
					found.name === found.email
						? found.email
						: `${found.firstname ?? ""} ${found.lastname ?? ""}`.trim() || found.email;
				return {
					...found,
					name: display,
					email: found.email ?? "",
				};
			})
			.filter((user): user is UserToAdd => user !== null);
		setUsersToAdd(mappedValue);
	};

	const initUsersForAdd = () => {
		axios
			.get(apiRoutes.getUsers, {
				headers: authHeader,
			})
			.then((res) => {
				const users = res.data.filter((user: GroupUser) => {
					return !groupUsers.some((groupUser) => groupUser.id === user.id);
				});
				const mappedUsers: UserToAdd[] = users.map((user: GroupUser) => {
					return {
						id: user.id,
						name: user.firstname && user.lastname ? `${user.firstname} ${user.lastname}` : user.email || "",
						email: user.email || "",
						firstname: user.firstname,
						lastname: user.lastname,
					};
				});
				setAvailableUsers(mappedUsers);
				setOptionsLoading(false);
			})
			.catch((err: AxiosError) => {
				console.error(err);
			});
	};

	const bulkRemoveUsersFromGroup = () => {
		if (!groupId) return;
		
		const userIds = gridRowSelectionIdsArray(selectedUsers, groupUsers);
		const deleteData = { userIds };
		const plural = userIds.length > 1;
		axios.delete(apiRoutes.bulkRemoveUsersFromGroup(groupId), { headers: authHeader, data: deleteData })
			.then(() => {
				init();
				setSelectedUsers(emptyGridRowSelectionModel());
				setRemoveUsersIsToggled(false);
			})
			.catch((err: AxiosError) => {
				console.error(err);
				handleToastAlert(messageLevels.ERROR, messages.USER_REMOVED_ERROR_MSG);
			});
	};

	const toggleAddNewDialog = () => {
		if (!addUsersToggled) {
			initUsersForAdd();
			setAddUsersToggled(true);
		} else {
			setAddUsersToggled(false);
			setAvailableUsers([]);
			setUsersToAdd([]);
			setOptionsLoading(true);
			setUserSeed(userSeed + 1);
		}
	};

	useEffect(() => {
		init();
		handleInitializeSortModel();
	}, [userSeed, state.name]);

	const {
		apiRef,
		gridFilterModel,
		onFilterModelChange,
		quickFilterSearchProps,
		visibleRowCount,
		filterControl,
		filterButtonEl,
	} = useCoAppDataGridFilters(groupUsers, loading);
	const selectedUserCount = resolveGridSelectedRowIds(selectedUsers, groupUsers).length;

	if (groupId) {
		return (
			<>
				<CoAppDatagridHeader
					title={`${state.name} User Management`}
					subResourceType="User"
					resourceType="Group"
					searchIsPresent={true}
					{...quickFilterSearchProps}
					searchPlaceholder="Search all columns…"
					filterControl={filterControl}
					addOnClickHandler={toggleAddNewDialog}
					addIsPresent={organizationSsoStatus ? false : true}
					editIsPresent={true}
					editOnClickHandler={handleToggleGroupEditDialog}
					deleteIsPresent={true}
					deleteOnClickHandler={handleToggleDeleteGroupDialog}
				/>
				<CoAppStandardDataGrid
					apiRef={apiRef}
					filterButtonEl={filterButtonEl}
					filterModel={gridFilterModel}
					onFilterModelChange={onFilterModelChange}
					visibleRowCount={visibleRowCount}
					columns={columns}
					rows={groupUsers}
					pinnedColumns={["actions"]}
					allowSelection={organizationSsoStatus ? false : true}
					targetResource="users"
					parentResource="group"
					parentResourceName={state.name}
					selectionModel={selectedUsers}
					setSelectionModel={setSelectedUsers}
					loading={loading}
					sortModel={sortModel}
					handleSortModelChange={handleSortModelChange}
					selectionFooter={
						<SettingsDataGridSelectionRow
							resourceNoun="user"
							visibleCount={visibleRowCount}
							selectedCount={selectedUserCount}
							loading={loading}
							onClearSelection={() => setSelectedUsers(emptyGridRowSelectionModel())}
							actions={organizationSsoStatus ? undefined : [{
								label: "Remove",
								icon: <Delete sx={destructiveIconSx} />,
								onClick: () => setRemoveUsersIsToggled(true),
								variant: "destructive",
							}]}
						/>
					}
				/>
				<CoAppAddItemsToListModal
					addButtonTitle="Save"
					inputLabel="User(s)"
					addItemsIsToggled={addUsersToggled}
					addItemsToCollection={addUsersToGroup}
					addItemsToNewArray={addUserToUsersToAdd}
					dialogTitle={`Add user(s) to ${state.name} group`}
					items={availableUsers}
					itemsToAdd={usersToAdd}
					toggleDialog={toggleAddNewDialog}
					loading={optionsLoading}
				/>
				<CoAppDestructiveConfirmationModal
					dialogOpen={deleteGroupIsToggled}
					dialogTitle={`Delete ${state.name} group?`}
					dialogMessage={messages.DELETION_CONFIRMATION_MSG(state.name, "group")}
					confirmClickHandler={confirmGroupDeletion}
					cancelClickHandler={handleToggleDeleteGroupDialog}
					actionText="Delete"
				/>
				<CoAppDestructiveConfirmationModal
					dialogOpen={removeUsersIsToggled}
					dialogTitle={`Remove ${gridRowSelectionSize(selectedUsers, groupUsers) > 1 ? "users" : "user"} from ${state.name} group?`}
					dialogMessage={`Are you sure that you want to remove the selected ${gridRowSelectionSize(selectedUsers, groupUsers) > 1 ? "users" : "user"} from the ${state.name} group?`}
					confirmClickHandler={bulkRemoveUsersFromGroup}
					cancelClickHandler={() => setRemoveUsersIsToggled(false)}
					actionText="Remove"
				/>
				<CoAppEditNameModal
					dialogOpen={editGroupIsToggled}
					dialogTitle="Edit group name"
					changeHandler={handleGroupNameChange}
					placeholderText={state.name}
					confirmClickHandler={handleGroupNameChangeSubmit}
					cancelClickHandler={handleToggleGroupEditDialog}
					actionText="Save"
					editNameError={groupNameError}
					setEditNameError={setGroupNameError}
				/>
			</>
		);
	} else {
		return (
			<Container sx={{ maxWidth: "150px!important", marginTop: "50px" }}>
				<Stack spacing={2}>
					<CircularProgress sx={{ color: "#2FBD70" }} />
				</Stack>
			</Container>
		);
	}
}